import {
  pgTable,
  text,
  serial,
  integer,
  decimal,
  varchar,
  date,
  timestamp,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Per-client subscription state. One row per client; updated as plans change.
export const clientSubscriptions = pgTable(
  "client_subscriptions",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    planName: text("plan_name").notNull(),
    monthlyAmount: decimal("monthly_amount", { precision: 10, scale: 2 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("active"), // active | past_due | cancelled | paused
    billingCycleStart: date("billing_cycle_start"),
    billingCycleEnd: date("billing_cycle_end"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    clientIdx: index("idx_client_subscriptions_client").on(t.clientId),
  })
);

export type ClientSubscription = typeof clientSubscriptions.$inferSelect;

// Invoice + payment history for the billing history tab. Read-only on the
// admin side this commit — actual payment processing is a separate concern.
export const clientBillingInvoices = pgTable(
  "client_billing_invoices",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    invoiceNumber: text("invoice_number").notNull(),
    invoiceDate: date("invoice_date").notNull(),
    dueDate: date("due_date"),
    description: text("description"),
    type: varchar("type", { length: 20 }).notNull().default("invoice"), // invoice | payment
    method: varchar("method", { length: 40 }), // ACH | Credit Card | Check | Wire
    status: varchar("status", { length: 20 }).notNull().default("open"), // paid | open | past_due | void
    amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
    periodStart: date("period_start"),
    periodEnd: date("period_end"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => ({
    clientIdx: index("idx_client_billing_invoices_client").on(t.clientId),
    clientDateIdx: index("idx_client_billing_invoices_client_date").on(t.clientId, t.invoiceDate),
    statusIdx: index("idx_client_billing_invoices_status").on(t.status),
  })
);

export const insertClientBillingInvoiceSchema = createInsertSchema(clientBillingInvoices).omit({
  id: true,
  createdAt: true,
});

export type ClientBillingInvoice = typeof clientBillingInvoices.$inferSelect;
export type InsertClientBillingInvoice = z.infer<typeof insertClientBillingInvoiceSchema>;

export const insertClientSubscriptionSchema = createInsertSchema(clientSubscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertClientSubscription = z.infer<typeof insertClientSubscriptionSchema>;
